import { useEffect, useRef, useState } from "react";
import { useDrillStore } from "@/application/stores/drillStore";
import { useEditorStore } from "@/application/stores/editorStore";
import { useDrillSession } from "@/application/usecases/useDrillSession";
import type { Drill } from "@/domain/learning/model/drill";
import type { CommandSet } from "@/domain/vim/model/command-set";
import type { RawKeyInput } from "@/domain/vim/model/keystroke";
import { Button } from "@/presentation/components/Button";
import { Meter } from "@/presentation/components/Meter";
import { useOverlay } from "@/presentation/components/overlay/OverlayProvider";
import { Board } from "@/presentation/features/drill/Board";
import { DrillProgress } from "@/presentation/features/drill/DrillProgress";
import { KeystrokeStrip } from "@/presentation/features/drill/KeystrokeStrip";
import type { KeystrokeOutcome } from "@/presentation/features/drill/keystroke-log";
import { TargetPanel } from "@/presentation/features/drill/TargetPanel";
import "./DrillView.css";

const AUTO_ADVANCE_DELAY_MS = 2400;
const KEYSTROKE_ANNOUNCE_INTERVAL = 5;

export type DrillVariant = "drill" | "exam";

export interface DrillProgressInfo {
  readonly current: number;
  readonly total: number;
}

interface DrillViewProps {
  readonly drill: Drill;
  readonly commandSet: CommandSet;
  /** 稽古（既定）か昇段試験か。差分は見た目のみ（ui-design.md §7.4）。 */
  readonly variant?: DrillVariant;
  /** `passed` から{@link AUTO_ADVANCE_DELAY_MS}後に呼ばれる。保存自体はこのフックの外側の責務。 */
  readonly onPassed?: () => void;
  /**
   * 1打ごとの分類結果（計画13で追加）。師範代の「未習得キー」反応・打ち音（技が確定した
   * 瞬間のみ）・無操作タイマーのリセットなど、演習の外側の関心事のために公開する。
   */
  readonly onOutcome?: (outcome: KeystrokeOutcome) => void;
  /** レッスン／試験の中断（終了）を要求する。Shift+Esc、および墨板外での Esc。 */
  readonly onRequestExit?: () => void;
  /** レッスン内（または試験内）の進み具合。昇段試験では出さない（ui-design.md §7.4）。 */
  readonly progress?: DrillProgressInfo;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

function isBoardTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && target.getAttribute("role") === "application";
}

function isOverlayOpen(): boolean {
  return Boolean(document.querySelector(".modal-panel, .popover, [role='dialog']"));
}

function toRawKeyInput(event: KeyboardEvent): RawKeyInput {
  return {
    key: event.key,
    code: event.code,
    ctrl: event.ctrlKey,
    alt: event.altKey,
    meta: event.metaKey,
    isComposing: event.isComposing,
  };
}

/**
 * 稽古（V-2）/ 昇段試験（V-3）の中身。墨板・的・打鍵の帯・手数メーター・判定の触れを束ねる。
 * S-2の状態機械（話↔稽古の切替・段からの離脱）は計画12の責務であり、ここでは持たない。
 */
export function DrillView({
  drill,
  commandSet,
  variant = "drill",
  onPassed,
  onOutcome,
  onRequestExit,
  progress,
}: DrillViewProps) {
  const { strip, handleKeydown, reset } = useDrillSession(drill, commandSet, onOutcome);
  const { showToast } = useOverlay();

  const buffer = useEditorStore((state) => state.editorState?.buffer.lines);
  const cursor = useEditorStore((state) => state.editorState?.cursor);
  const usedKeystrokes = useEditorStore((state) => state.editorState?.usedKeystrokes ?? 0);
  const verdict = useDrillStore((state) => state.verdict);

  const [announcedCount, setAnnouncedCount] = useState<number | null>(null);

  useEffect(() => {
    if (usedKeystrokes > 0 && usedKeystrokes % KEYSTROKE_ANNOUNCE_INTERVAL === 0) {
      setAnnouncedCount(usedKeystrokes);
    }
  }, [usedKeystrokes]);

  const onPassedRef = useRef(onPassed);
  onPassedRef.current = onPassed;
  const onRequestExitRef = useRef(onRequestExit);
  onRequestExitRef.current = onRequestExit;
  const handleKeydownRef = useRef(handleKeydown);
  handleKeydownRef.current = handleKeydown;
  const verdictRef = useRef(verdict);
  verdictRef.current = verdict;

  useEffect(() => {
    if (verdict?.kind === "passed") {
      showToast({ kind: "pass", message: "よい手だ" });
      const timer = window.setTimeout(() => onPassedRef.current?.(), AUTO_ADVANCE_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
    if (verdict?.kind === "over-limit") {
      showToast({
        kind: "over-limit",
        message:
          variant === "exam" ? "条件は満たした。朱印にはあと一歩" : "惜しい。もっと短い手がある",
      });
    }
    return undefined;
  }, [verdict, showToast, variant]);

  // フォーカス位置に関わらずアプリ操作のキーを効かせる（AdvSceneのEnter送りと同じ作法）。
  // キャプチャ段階で受け取り、墨板へ転送する打鍵がボタンの既定動作と二重に発火しないようにする。
  useEffect(() => {
    function handleGlobalKeyDown(event: KeyboardEvent) {
      if (event.isComposing || event.key === "Process") {
        return;
      }
      if (isOverlayOpen()) {
        return;
      }

      if (event.key === "Escape" && event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();
        onRequestExitRef.current?.();
        return;
      }

      if (event.key === "Escape" && !isBoardTarget(event.target)) {
        event.preventDefault();
        event.stopPropagation();
        onRequestExitRef.current?.();
        return;
      }

      if (event.key === "Enter" && verdictRef.current?.kind === "passed") {
        event.preventDefault();
        event.stopPropagation();
        onPassedRef.current?.();
        return;
      }

      // 墨板がキーを専有している間は、墨板自身のハンドラに任せる。
      if (isBoardTarget(event.target) || isEditableTarget(event.target)) {
        return;
      }
      if (event.key === "Tab" || event.metaKey || event.altKey) {
        return;
      }
      // 焦点が押しにあるときの Enter / Space は、その押しの起動を優先する。
      if (
        (event.key === "Enter" || event.key === " ") &&
        event.target instanceof HTMLElement &&
        event.target.closest("button, a, [role='button']")
      ) {
        return;
      }
      if (
        event.key === "Shift" ||
        event.key === "Control" ||
        event.key === "Alt" ||
        event.key === "Meta"
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      const board = document.querySelector<HTMLElement>('[role="application"]');
      board?.focus();
      handleKeydownRef.current(toRawKeyInput(event));
    }

    document.addEventListener("keydown", handleGlobalKeyDown, true);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
  }, []);

  if (!buffer || !cursor) {
    return null;
  }

  const achieved = verdict?.kind === "passed" || verdict?.kind === "over-limit";
  const showProgress = variant === "drill" && progress !== undefined && progress.total > 0;

  return (
    <div
      className={["drill-view", variant === "exam" ? "drill-view--exam" : null]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="drill-view__header-row">
        <p className="drill-view__instruction">{drill.instruction}</p>
        {showProgress && <DrillProgress current={progress.current} total={progress.total} />}
      </div>
      <div className="drill-view__stage">
        <Board onKeydown={handleKeydown} pulse={verdict?.kind === "passed"} />
        <TargetPanel
          goal={drill.goal}
          currentBuffer={buffer}
          currentCursor={cursor}
          achieved={achieved}
        />
      </div>
      <KeystrokeStrip frames={strip} />
      <div className="drill-view__footer">
        <Meter used={usedKeystrokes} limit={drill.keystrokeLimit} />
        {/*
          やり直しは常に手の届くところに置く。手数を使い切る前でも「今の紙面を捨てて
          最初から組み立て直したい」場面は必ずあり、そのたびに演習を抜けさせるのは体験を損なう。
          手数超過のときだけ、次の一手を促す文言（「もう一度」）へ切り替える。
        */}
        {verdict?.kind !== "passed" && (
          // キー表記は付けない。Backspaceは墨板がvimの `<BS>` として受け取るため、
          // やり直しの近道にはならない（画面上の押しだけが唯一の経路）。
          <Button variant="ghost" icon="reset" onClick={reset}>
            {verdict?.kind === "over-limit" ? "もう一度" : "はじめから"}
          </Button>
        )}
        {verdict?.kind === "passed" && (
          <Button variant="primary" keyHint="⏎" onClick={() => onPassedRef.current?.()}>
            次へ
          </Button>
        )}
      </div>
      <span className="drill-view__sr-only" aria-live="polite">
        {announcedCount !== null ? `手数 ${announcedCount}` : ""}
      </span>
    </div>
  );
}
